import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Linkedin, Mail, Clock, Wand2, Users, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState({ configured: false, connected: false });
  const [saving, setSaving] = useState(false);
  const [digestRunning, setDigestRunning] = useState(false);
  const [recentDigests, setRecentDigests] = useState([]);
  const [team, setTeam] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("linkedin") === "connected") toast.success("LinkedIn connected");
  }, [params]);

  const load = async () => {
    try {
      const [c, l, d] = await Promise.all([
        api.get("/config"),
        api.get("/linkedin/status"),
        api.get("/digest"),
      ]);
      setCfg(c.data);
      setLinkedinStatus(l.data);
      setRecentDigests(d.data || []);
      if (user?.role === "admin") {
        try {
          const t = await api.get("/team");
          setTeam(t.data || []);
        } catch { /* non-admin */ }
      }
    } catch (e) {
      toast.error("Failed to load settings");
    }
  };
  useEffect(() => { load(); }, [user]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put("/config", {
        cron_interval_minutes: Number(cfg.cron_interval_minutes) || 60,
        digest_enabled: !!cfg.digest_enabled,
        digest_hour_utc: Number(cfg.digest_hour_utc) || 6,
        digest_recipient: cfg.digest_recipient || "",
        linkedin_connected: linkedinStatus.connected,
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const connectLinkedIn = async () => {
    try {
      const { data } = await api.get("/linkedin/oauth/start");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LinkedIn not configured yet");
    }
  };

  const runDigestNow = async () => {
    setDigestRunning(true);
    try {
      const { data } = await api.post("/digest/run-now");
      if (data.topics?.length) {
        toast.success(`Digest sent (${data.topics.length} topics)`);
      } else {
        toast.error("No topics extracted — try again later");
      }
      load();
    } catch {
      toast.error("Digest run failed");
    } finally {
      setDigestRunning(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// settings</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          Controls.
        </h1>
        <p className="mt-3 text-bone/60 max-w-xl">
          Tune cron cadence, daily digest, and connect LinkedIn (once your Marketing API access is approved).
        </p>

        {/* Cron */}
        <Section icon={Clock} title="Background worker">
          <Row label="Cron interval (minutes)" hint="How often the scheduler checks for due articles.">
            <Input
              type="number"
              min={5}
              max={1440}
              value={cfg.cron_interval_minutes}
              onChange={(e) => setCfg({ ...cfg, cron_interval_minutes: Number(e.target.value) })}
              className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10 w-32"
              data-testid="settings-cron-interval"
            />
          </Row>
        </Section>

        {/* Digest */}
        <Section icon={Mail} title="Daily trending digest">
          <Row label="Enabled" hint="Sends 3 fresh topic ideas every morning.">
            <Switch
              checked={!!cfg.digest_enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, digest_enabled: v })}
              data-testid="settings-digest-enabled"
            />
          </Row>
          <Row label="Send at (UTC hour, 0-23)">
            <Input
              type="number"
              min={0}
              max={23}
              value={cfg.digest_hour_utc}
              onChange={(e) => setCfg({ ...cfg, digest_hour_utc: Number(e.target.value) })}
              className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10 w-24"
              data-testid="settings-digest-hour"
            />
          </Row>
          <Row label="Recipient email">
            <Input
              type="email"
              value={cfg.digest_recipient || ""}
              onChange={(e) => setCfg({ ...cfg, digest_recipient: e.target.value })}
              placeholder="admin@wisewit.com"
              className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10 w-full max-w-sm"
              data-testid="settings-digest-recipient"
            />
          </Row>
          <Button
            onClick={runDigestNow}
            disabled={digestRunning}
            data-testid="settings-run-digest-btn"
            className="mt-3 rounded-none bg-safety hover:bg-safety/90 text-white label-mono h-10"
          >
            {digestRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Run digest now
          </Button>

          {recentDigests.length > 0 && (
            <div className="mt-6 space-y-2">
              <div className="label-mono text-bone/50">// recent</div>
              {recentDigests.slice(0, 3).map((d) => (
                <div key={d.id} className="border border-hairline bg-surface p-3 text-sm">
                  <div className="label-mono text-[10px] text-bone/50">
                    {String(d.created_at).slice(0, 16)} · {d.emailed ? "emailed" : "stored"} → {d.recipient}
                  </div>
                  <ul className="mt-2 list-disc list-outside ml-4 text-bone/80 space-y-1">
                    {(d.topics || []).map((t, i) => (<li key={i}>{t}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Team workspace */}
        {user?.role === "admin" && (
          <Section icon={Users} title="Team workspace">
            <div className="flex items-center justify-between border-b border-hairline pb-3">
              <div>
                <div className="label-mono text-bone/80">Writers & admins</div>
                <div className="text-xs text-bone/45 mt-1">Each member sees only their own articles. Admins can manage the team.</div>
              </div>
              <Button
                onClick={() => setInviteOpen(true)}
                data-testid="team-invite-btn"
                className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add member
              </Button>
            </div>
            <div className="border border-hairline overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface label-mono text-bone/60 text-[10px]">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {team.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-bone/50 label-mono text-xs">No team members yet.</td></tr>
                  )}
                  {team.map((u) => (
                    <tr key={u.id} className="border-t border-hairline" data-testid={`team-row-${u.id}`}>
                      <td className="p-3">{u.name}</td>
                      <td className="p-3 text-bone/70 label-mono text-xs">{u.email}</td>
                      <td className="p-3">
                        <span className={`label-mono text-[10px] px-2 py-1 border ${u.role === "admin" ? "border-safety text-safety" : "border-hairline text-bone/70"}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`label-mono text-[10px] px-2 py-1 border ${u.is_active ? "border-emerald-500/60 text-emerald-300" : "border-hairline text-bone/50"}`}>
                          {u.is_active ? "active" : "deactivated"}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {u.id !== user.id && (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm" variant="outline"
                              onClick={async () => {
                                try {
                                  await api.patch(`/team/${u.id}`, { is_active: !u.is_active });
                                  toast.success(u.is_active ? "Deactivated" : "Reactivated");
                                  load();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
                              }}
                              data-testid={`team-toggle-${u.id}`}
                              className="rounded-none border-hairline bg-transparent label-mono h-8 text-[10px]"
                            >
                              {u.is_active ? "Deactivate" : "Reactivate"}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* LinkedIn */}
        <Section icon={Linkedin} title="LinkedIn Marketing API">
          {!linkedinStatus.configured ? (
            <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm text-yellow-100">
              <div className="label-mono text-yellow-300 mb-1">// not configured</div>
              Set <code>LINKEDIN_CLIENT_ID</code>, <code>LINKEDIN_CLIENT_SECRET</code>, and{" "}
              <code>LINKEDIN_ORGANIZATION_URN</code> in <code>backend/.env</code> after your LinkedIn
              Developer App is approved for the Community Management API. Then restart the backend
              and click <b>Connect</b> below.
            </div>
          ) : linkedinStatus.connected ? (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-100" data-testid="linkedin-connected">
              <div className="label-mono text-emerald-300 mb-1">// connected</div>
              Token valid until {linkedinStatus.expires_at?.slice(0, 16)}.
              Organization URN: <code>{linkedinStatus.organization_urn || "(set in .env)"}</code>
            </div>
          ) : (
            <Button
              onClick={connectLinkedIn}
              data-testid="settings-linkedin-connect-btn"
              className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11"
            >
              <Linkedin className="w-4 h-4 mr-2" /> Connect LinkedIn
            </Button>
          )}
        </Section>

        <div className="mt-10 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="settings-save-btn"
            className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11 px-8"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
        </div>
      </main>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => { setInviteOpen(false); load(); }}
      />
    </div>
  );
}

function InviteDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "writer" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", password: "", role: "writer" });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Name, email, and 8+ character password required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/team", form);
      toast.success(`Invited ${form.email}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="team-invite-dialog" className="rounded-none bg-ink border-hairline text-bone max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-bone">Add a team member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-mono text-bone/70">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="invite-name" />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="invite-email" />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Temporary password (8+ chars)</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10" data-testid="invite-password" />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1 rounded-none bg-surface border-hairline" data-testid="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-surface border-hairline">
                <SelectItem value="writer" className="rounded-none">Writer</SelectItem>
                <SelectItem value="admin" className="rounded-none">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-hairline bg-transparent label-mono" data-testid="invite-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono" data-testid="invite-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Invite
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <section className="mt-10 border-t border-hairline pt-6">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-safety" />
      <h2 className="font-display text-2xl font-bold tracking-tightest">{title}</h2>
    </div>
    <div className="mt-5 space-y-4">{children}</div>
  </section>
);

const Row = ({ label, hint, children }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-hairline pb-4">
    <div>
      <Label className="label-mono text-bone/80">{label}</Label>
      {hint && <div className="text-xs text-bone/45 mt-1">{hint}</div>}
    </div>
    <div>{children}</div>
  </div>
);
